// <s-ring-sizer> — ring size helper inside the size guide dialog.
// Screen method: the shopper calibrates CSS pixels per millimetre against a €1 coin (23.25 mm),
// then matches a circle to the inside of a ring they own.
// Sizes are read from the chart rows (data-size, data-circumference = inner circumference in mm).
const COIN_MM = 23.25;
const STORAGE_KEY = 'pime_ring_sizer_px_per_mm';

class SRingSizer extends HTMLElement {
  #ac;
  #sizes = [];
  #locale = 'pt-PT';

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.#locale = document.documentElement.lang || 'pt-PT';

    // The conversion chart rows are the single source of sizes, so chart and calculators always agree.
    this.#sizes = [...this.querySelectorAll('[data-size-row]')]
      .map((row) => ({ s: row.dataset.size || '', c: parseFloat(row.dataset.circumference || '') }))
      .filter((size) => size.s && Number.isFinite(size.c))
      .sort((a, b) => a.c - b.c);

    this.coin = this.querySelector('[data-coin]');
    this.coinRange = this.querySelector('[data-coin-range]');
    this.circle = this.querySelector('[data-circle]');
    this.ringRange = this.querySelector('[data-ring-range]');
    this.screenResult = this.querySelector('[data-screen-result]');

    // Calibration is per device, so remember it between visits.
    const saved = this.#readSaved();
    if (saved && this.coinRange) this.coinRange.value = String(saved);

    this.coinRange?.addEventListener('input', () => this.#onCoin(), { signal });
    this.ringRange?.addEventListener('input', () => this.#renderScreen(), { signal });

    // Tape method: finger circumference in cm (paper strip or string) maps straight to a size.
    this.tapeInput = this.querySelector('[data-tape-input]');
    this.tapeResult = this.querySelector('[data-tape-result]');
    this.tapeInput?.addEventListener('input', () => this.#renderTape(), { signal });

    this.#renderScreen();
  }

  #renderTape() {
    if (!this.tapeResult) return;
    const raw = (this.tapeInput?.value || '').replace(',', '.');
    const value = parseFloat(raw);
    // The field asks for centimetres (e.g. 5,4); a value above 20 can only be millimetres (e.g. 54), so accept both.
    const circumference = value > 20 ? value : value * 10;
    if (!Number.isFinite(circumference) || circumference <= 0) {
      this.tapeResult.textContent = '';
      return;
    }
    const size = this.#closestByCircumference(circumference);
    this.tapeResult.textContent = size
      ? (this.dataset.tapeResultTemplate || '[size]').replace('[size]', size.s)
      : this.dataset.outOfRange || '';
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #pxPerMm() {
    return parseFloat(this.coinRange?.value || '') || 3.78;
  }

  #readSaved() {
    try {
      return parseFloat(window.localStorage.getItem(STORAGE_KEY) || '') || null;
    } catch {
      return null;
    }
  }

  #onCoin() {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(this.#pxPerMm));
    } catch {
      // Storage unavailable: calibration still works for this visit.
    }
    this.#renderScreen();
  }

  #format(value) {
    return value.toLocaleString(this.#locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // The shopper matched the ring itself, so pick the closest size; a tie (halfway) takes the larger one.
  #closestByCircumference(circumference) {
    let best = null;
    for (const size of this.#sizes) {
      if (!best || Math.abs(size.c - circumference) <= Math.abs(best.c - circumference)) best = size;
    }
    // Same bounds as the chart ranges: 1 mm either side of the smallest and largest sizes.
    const smallest = this.#sizes[0];
    const largest = this.#sizes[this.#sizes.length - 1];
    if (!best || circumference < smallest.c - 1 || circumference >= largest.c + 1) return null;
    return best;
  }

  #renderScreen() {
    const pxPerMm = this.#pxPerMm;
    if (this.coin) {
      const coinPx = `${COIN_MM * pxPerMm}px`;
      this.coin.style.width = coinPx;
      this.coin.style.height = coinPx;
    }

    const diameter = parseFloat(this.ringRange?.value || '') || 17;
    if (this.circle) {
      const ringPx = `${diameter * pxPerMm}px`;
      this.circle.style.width = ringPx;
      this.circle.style.height = ringPx;
    }

    if (!this.screenResult) return;
    const size = this.#closestByCircumference(diameter * Math.PI);
    this.screenResult.textContent = size
      ? (this.dataset.resultTemplate || '[size]').replace('[size]', size.s).replace('[cm]', this.#format(diameter / 10))
      : this.dataset.outOfRange || '';
  }
}

if (!customElements.get('s-ring-sizer')) customElements.define('s-ring-sizer', SRingSizer);
